import React from 'react';
import { graphql } from 'gatsby';
import styled from "@emotion/styled";
import dimensions from 'styles/dimensions';
import Layout from 'components/Layout';
import SEO from 'components/SEO';
import Hero from 'components/Hero';
import Subnav from 'components/_page/product/Subnav';
import Accordion from 'components/Accordion';
import ContentList from 'components/ContentList';
import ContentBlock from 'components/ContentBlock';
import ContentColumns from 'components/ContentColumns';
import Tout from 'components/Tout';
import FeaturedCaseStudies from 'components/FeaturedCaseStudies';
import Trapdoor from 'components/Trapdoor';
import FeatureProducts from 'components/FeatureProducts';
import GettingStarted from 'components/_page/product/GettingStarted';
import FeaturedResources from 'components/FeaturedResources';

const ProductSection = styled.section`
  & + * {
    margin-top: 8rem;
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    & + * {
      margin-top: 10.4rem;
    }
  }
`;

export default ({ data }) => {
  const product = data.prismic.productByUID;
  const allProducts = data.prismic.allProductss.edges[0].node;

  if (!product) {
    return null;
  }

  const sections = [
    {
      label: 'Overview',
      anchor: 'overview',
      hasContent: product.overview_content,
    },
    {
      label: 'Features',
      anchor: 'features',
      hasContent: product.features_list && product.features_list.length > 0,
    },
    {
      label: 'Pricing',
      anchor: 'pricing',
      hasContent: product.pricing_heading || product.pricing_content,
    },
    {
      label: 'Products',
      anchor: 'products',
      hasContent: product.include_product_suite,
    },
    {
      label: 'Getting Started',
      anchor: 'getting-started',
      hasContent: product.get_started_list && product.get_started_list.length > 0,
    },
  ];

  return (
    <Layout>
      <SEO
        title={product.social_title || product.name}
        description={product.social_description ? product.social_description : null}
        image={product.social_image ? product.social_image.url : null}
      />

      <Hero
        rubric={product.name}
        heading={product.hero_heading}
        content={product.hero_content}
        image={product.hero_image}
        illustration={product.hero_illustration}
        imagePosition='right'
        size='large'
      />

      <Subnav sections={sections}/>

      {/* Overview */}
      {sections[0].hasContent && (
      <ProductSection id='overview'>
        {product.overview_content && (
          <ContentBlock
            intro={product.logline}
            heading={product.overview_heading}
            image={product.overview_image}
            illustration={product.overview_illustration}
            content={product.overview_content}
            variant='large'
          />
        )}

        {product.overview_secondary_heading && (
          <Tout
            intro={product.overview_secondary_intro}
            heading={product.overview_secondary_heading}
            content={product.overview_secondary_content}
          />
        )}

        {product.overview_list && (
          <ContentColumns items={product.overview_list}/>
        )}

        {product.overview_card_heading && (
          <Trapdoor
            heading={product.overview_card_heading}
            buttonText={product.overview_card_button_text}
            destination={product.overview_card_button_destination}
          />
        )}

        {(product.overview_case_study_list.length > 0 && product.overview_case_study_list[0].study) &&
        <FeaturedCaseStudies
          delay={allProducts.case_study_delay || 6000}
          slides={product.overview_case_study_list}
        />
        }
      </ProductSection>
      )}

      {/* Features */}
      {sections[1].hasContent &&
        <ProductSection id='features'>
          {product.dropdown_drawers && (
            <Accordion
              intro={product.features_intro}
              heading={product.features_heading}
              image={product.features_image}
              illustration={product.features_illustration}
              content={product.features_content}
              items={product.features_list}
              buttonText={product.features_button_text}
              buttonDestination={product.features_button_destination}
            />
          )}

          {!product.dropdown_drawers && (
            <ContentList
              intro={product.features_intro}
              heading={product.features_heading}
              image={product.features_image}
              illustration={product.features_illustration}
              content={product.features_content}
              items={product.features_list}
              buttonText={product.features_button_text}
              buttonDestination={product.features_button_destination}
            />
          )}
        </ProductSection>
      }

      {(product.case_study_list.length > 0 && product.case_study_list[0].study) &&
      <FeaturedCaseStudies
        delay={allProducts.case_study_delay || 6000}
        slides={product.case_study_list}
      />
      }

      {/* Pricing */}
      {sections[2].hasContent &&
      <Tout
        id='pricing'
        intro={product.pricing_rubric}
        heading={product.pricing_heading}
        content={product.pricing_content}
        buttonText={product.pricing_button_text}
        destination={product.pricing_button_url}
      />
      }

      {/* Products */}
      {sections[3].hasContent && (
        <ProductSection id='products'>
          <FeatureProducts
            heading={allProducts.heading}
            content={allProducts.content}
            products={allProducts.products_list}
            variant='product'
          />
        </ProductSection>
      )}

      {/* Getting Started */}
      {sections[4].hasContent && (
      <GettingStarted
        content={product.get_started_content}
        items={product.get_started_list}
        buttonText={product.get_started_button_text || 'Get Started'}
        buttonDestination={product.get_started_destination || '/pricing'}
      />
      )}

      {/* Featured Resources */}
      {product.resources_heading && (
        <FeaturedResources
          caption={product.resources_caption}
          heading={product.resources_heading}
          intro={product.resources_intro}
          widget={product.resources_widget_name}
        />
      )}
    </Layout>
  );
};

export const query = graphql`
  query getProduct($uid: String!) {
    prismic {
      productByUID: product(uid: $uid, lang: "en-us") {
        name
        logline
        hero_heading
        hero_content
        hero_image
        hero_illustration {
          ... on PRISMIC__FileLink {
            _linkType
            name
            size
            url
          }
        }
        include_product_suite
        overview_intro
        overview_heading
        overview_image
        overview_illustration {
          ... on PRISMIC__FileLink {
            _linkType
            name
            size
            url
          }
        }
        overview_content
        overview_list {
          content
          heading
          image
        }
        overview_secondary_intro
        overview_secondary_heading
        overview_secondary_content
        overview_case_study_list {
          study {
            ... on PRISMIC_Case_study {
              company
              logo
              quote
              content
              image
              statistics {
                label
                value
              }
            }
          }    
        }
        overview_card_heading
        overview_card_button_destination
        overview_card_button_text
        features_intro
        features_heading
        features_content
        features_image
        features_illustration {
          ... on PRISMIC__FileLink {
            _linkType
            name
            size
            url
          }
        }
        dropdown_drawers
        features_list {
          heading
          content
          additional_content
          image
          illustration {
            ... on PRISMIC__FileLink {
              _linkType
              name
              size
              url
            }
          }
        }
        features_button_destination
        features_button_text
        case_study_list {
          study {
            ... on PRISMIC_Case_study {
              company
              logo
              quote
              content
              image
              statistics {
                label
                value
              }
            }
          }        
        }
        pricing_rubric
        pricing_heading
        pricing_content
        pricing_button_url
        pricing_button_text
        get_started_content
        get_started_list {
          heading
          text
        }
        get_started_button_text
        get_started_destination
        resources_intro
        resources_heading
        resources_caption
        resources_widget_name
      }
      allProductss {
        edges {
          node {
            heading
            content
            products_list {
              product {
                ... on PRISMIC_Product {
                  logo
                  name
                  logline
                  short_description
                  preview_image
                  preview_hover_image
                  _meta {
                    uid
                  }
                }
              }
            }
            case_study_delay
          }
        }
      }
    }
  }
`;
